const EMPTY_OBJECT = {};

const getAnswerRecord = (snapshot, fieldKey) => {
  const answers = snapshot?.snapshot_json?.answers;
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return null;
  return answers[fieldKey] || null;
};

const getSnapshotValue = (snapshot, fieldKey) => {
  const answer = getAnswerRecord(snapshot, fieldKey);
  return answer?.value ?? undefined;
};

const getValue = (snapshot, decisions, fieldKey) => {
  const decisionValue = decisions?.[fieldKey];
  if (decisionValue !== undefined && decisionValue !== null && decisionValue !== '') return decisionValue;
  return getSnapshotValue(snapshot, fieldKey);
};

const isPresent = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== '';
};

const asBoolean = (value) => value === true || value === 'true' || value === 'Yes';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const isMarriedLike = (value) => {
  const normalized = normalizeText(value);
  return ['married', 'remarried', 'domestic partner'].includes(normalized);
};

const originalAnswersFor = (snapshot, decisions, fieldKeys) => {
  return fieldKeys.reduce((answers, fieldKey) => {
    answers[fieldKey] = {
      intake: getSnapshotValue(snapshot, fieldKey) ?? null,
      decision: decisions?.[fieldKey] ?? null
    };
    return answers;
  }, {});
};

const createItem = ({
  category,
  itemKey,
  title,
  description,
  fieldKeys = [],
  warningLevel = 'info',
  required = false,
  blocksCompletion = false,
  sortOrder
}, snapshot, decisions) => ({
  category,
  item_key: itemKey,
  title,
  description,
  source_rule_key: itemKey,
  related_field_keys: fieldKeys,
  original_answer_json: originalAnswersFor(snapshot, decisions, fieldKeys),
  warning_level: warningLevel,
  is_required: required,
  blocks_completion: blocksCompletion,
  sort_order: sortOrder
});

export const buildAttorneyReviewItems = ({ matter, client, intakeSnapshot, estatePlanDecisions }) => {
  const decisions = estatePlanDecisions || EMPTY_OBJECT;
  const maritalStatus = getValue(intakeSnapshot, decisions, 'marital_status');
  const hasChildren = asBoolean(getValue(intakeSnapshot, decisions, 'has_children'));
  const hasMinorChildren = asBoolean(getValue(intakeSnapshot, decisions, 'has_minor_children'));
  const spouseHasChildren = asBoolean(getValue(intakeSnapshot, decisions, 'spouse_has_children'));
  const hasSpecificGifts = asBoolean(getValue(intakeSnapshot, decisions, 'has_specific_gifts'));
  const residuePlan = getValue(intakeSnapshot, decisions, 'residue_distribution_plan');
  const hasPatientAdvocate = asBoolean(getValue(intakeSnapshot, decisions, 'client_has_patient_advocate'));
  const hasDurablePowerOfAttorney = asBoolean(getValue(intakeSnapshot, decisions, 'client_has_durable_power_of_attorney'));

  const items = [
    createItem({
      category: 'Client Information',
      itemKey: 'client_identity_review',
      title: 'Confirm client identity and demographic information',
      description: 'Review the client name, date of birth, county, state of residence, email, and phone before drafting.',
      fieldKeys: [
        'testator_first_name',
        'testator_middle_initial',
        'testator_last_name',
        'testator_name',
        'client_birthday',
        'county'
      ],
      required: true,
      blocksCompletion: true,
      sortOrder: 10
    }, intakeSnapshot, decisions),
    createItem({
      category: 'Plan Type',
      itemKey: 'plan_type_review',
      title: 'Confirm requested plan type and governing state',
      description: 'Make sure the selected package and governing state match the client facts and advisor expectations.',
      fieldKeys: ['will_type', 'has_living_trust', 'trust_type'],
      required: true,
      blocksCompletion: true,
      sortOrder: 20
    }, intakeSnapshot, decisions),
    createItem({
      category: 'Fiduciaries',
      itemKey: 'personal_representative_review',
      title: 'Review personal representative choices',
      description: 'Confirm the primary and alternate personal representative choices are complete and appropriate.',
      fieldKeys: ['personal_representative_primary', 'personal_representative_alternate'],
      required: true,
      blocksCompletion: true,
      sortOrder: 70
    }, intakeSnapshot, decisions),
    createItem({
      category: 'Final Arrangements',
      itemKey: 'final_arrangements_review',
      title: 'Review final arrangement preferences',
      description: 'Check burial, cremation, anatomical gift, and related preference fields for drafting relevance.',
      fieldKeys: ['final_arrangement_preference', 'burial_location', 'anatomical_gift_preference'],
      sortOrder: 120
    }, intakeSnapshot, decisions),
    createItem({
      category: 'Delivery',
      itemKey: 'draft_delivery_review',
      title: 'Review draft delivery instructions',
      description: 'Confirm email, mail, and additional note fields before sending drafts.',
      fieldKeys: ['email_drafts_to', 'mail_drafts_to', 'additional_notes'],
      sortOrder: 140
    }, intakeSnapshot, decisions)
  ];

  if (isMarriedLike(maritalStatus)) {
    items.push(createItem({
      category: 'Spouse',
      itemKey: 'spouse_information_review',
      title: 'Review spouse information',
      description: 'Confirm spouse name, date of birth, and whether spouse-related drafting provisions are needed.',
      fieldKeys: [
        'spouse_first_name',
        'spouse_middle_initial',
        'spouse_last_name',
        'spouse_name',
        'spouse_birthday',
        'spouse_will_differences'
      ],
      required: true,
      blocksCompletion: true,
      sortOrder: 30
    }, intakeSnapshot, decisions));
  }

  if (hasChildren) {
    items.push(createItem({
      category: 'Children',
      itemKey: 'children_information_review',
      title: 'Review children information',
      description: 'Confirm child names, dates of birth, total child count, and whether any child-specific drafting notes are needed.',
      fieldKeys: ['children', 'child_count', 'has_minor_children'],
      required: true,
      blocksCompletion: true,
      sortOrder: 40
    }, intakeSnapshot, decisions));
  }

  if (hasMinorChildren) {
    items.push(createItem({
      category: 'Children',
      itemKey: 'minor_children_guardian_review',
      title: 'Review minor-child guardian and conservator choices',
      description: 'Confirm guardian, alternate guardian, conservator, and any joint guardian preference.',
      fieldKeys: [
        'guardian_primary',
        'guardian_alternate',
        'uses_joint_guardians',
        'conservator_primary',
        'conservator_alternate'
      ],
      required: true,
      blocksCompletion: true,
      warningLevel: 'warning',
      sortOrder: 50
    }, intakeSnapshot, decisions));
  }

  if (spouseHasChildren) {
    items.push(createItem({
      category: 'Children',
      itemKey: 'spouse_children_review',
      title: 'Review spouse children from another relationship',
      description: 'Confirm whether blended-family drafting concerns or additional attorney notes are needed.',
      fieldKeys: ['spouse_has_children', 'blended_family', 'stepchildren_treatment'],
      warningLevel: 'warning',
      sortOrder: 55
    }, intakeSnapshot, decisions));
  }

  if (hasSpecificGifts) {
    items.push(createItem({
      category: 'Gifts',
      itemKey: 'specific_gifts_review',
      title: 'Review specific gifts and charitable gifts',
      description: 'Confirm each gift has enough detail to identify the property, beneficiary, and backup plan.',
      fieldKeys: ['specific_gifts', 'specific_gift_alternate_plan'],
      required: true,
      blocksCompletion: true,
      sortOrder: 80
    }, intakeSnapshot, decisions));
  }

  if (residuePlan === 'named_beneficiaries') {
    items.push(createItem({
      category: 'Distribution',
      itemKey: 'residue_named_beneficiaries_review',
      title: 'Review named residuary beneficiaries',
      description: 'Confirm names, relationships, and shares or percentages for each named beneficiary.',
      fieldKeys: ['residue_named_beneficiaries', 'residue_distribution_notes'],
      required: true,
      blocksCompletion: true,
      sortOrder: 90
    }, intakeSnapshot, decisions));
  }

  if (hasPatientAdvocate) {
    items.push(createItem({
      category: 'Health Care',
      itemKey: 'patient_advocate_review',
      title: 'Review patient advocate choices',
      description: 'Confirm the primary, secondary, and additional patient advocate selections.',
      fieldKeys: ['patient_advocate_primary', 'patient_advocate_secondary', 'patient_advocate_additional'],
      required: true,
      blocksCompletion: true,
      sortOrder: 100
    }, intakeSnapshot, decisions));
  }

  if (hasDurablePowerOfAttorney) {
    items.push(createItem({
      category: 'Financial Power of Attorney',
      itemKey: 'financial_agent_review',
      title: 'Review financial power of attorney agents',
      description: 'Confirm the primary, secondary, and additional financial agent selections.',
      fieldKeys: [
        'financial_power_of_attorney_primary',
        'financial_power_of_attorney_secondary',
        'financial_power_of_attorney_additional'
      ],
      required: true,
      blocksCompletion: true,
      sortOrder: 110
    }, intakeSnapshot, decisions));
  }

  if (!isPresent(client?.email) && !isPresent(getValue(intakeSnapshot, decisions, 'email_drafts_to'))) {
    items.push(createItem({
      category: 'Missing Information',
      itemKey: 'missing_email_review',
      title: 'Client email or draft delivery email is missing',
      description: 'Ask the advisor for a usable email address or confirm paper delivery.',
      fieldKeys: ['email_drafts_to'],
      warningLevel: 'warning',
      required: true,
      blocksCompletion: true,
      sortOrder: 150
    }, intakeSnapshot, decisions));
  }

  return items.map((item) => ({
    ...item,
    matter_id: matter.id,
    law_firm_id: matter.law_firm_id
  }));
};
